import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import Pagination from '../components/Pagination';
import { DashboardStats, Procedure } from '../types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  DocumentTextIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
  InboxIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';

// ─── Skeleton ────────────────────────────────────────────────────────────────

const SkeletonBox: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-xl ${className}`} />
);

const StatistiquesSkeleton: React.FC = () => (
  <div className="space-y-8">
    <div className="space-y-2">
      <SkeletonBox className="h-8 w-48" />
      <SkeletonBox className="h-4 w-72" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <SkeletonBox key={i} className="h-32" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SkeletonBox className="h-80" />
      <SkeletonBox className="h-80" />
    </div>
    <SkeletonBox className="h-96" />
    <SkeletonBox className="h-64" />
  </div>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

const getProgressBarColor = (value: number) => {
  if (value === 100) return 'from-green-400 to-green-600';
  if (value >= 60) return 'from-blue-400 to-blue-600';
  if (value >= 30) return 'from-amber-400 to-amber-500';
  return 'from-red-400 to-red-500';
};

const getProgressTextColor = (value: number) => {
  if (value === 100) return 'text-green-600';
  if (value >= 60) return 'text-blue-600';
  if (value >= 30) return 'text-amber-600';
  return 'text-red-500';
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

const CustomBarTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-3 text-sm">
        <p className="font-semibold text-gray-800 mb-1">{label}</p>
        {payload.map((item, i) => (
          <p key={i} className="text-gray-600 flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full inline-block"
              style={{ backgroundColor: item.color }}
            />
            {item.name}&nbsp;:&nbsp;<span className="font-medium text-gray-900">{item.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomPieTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0];
    return (
      <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-3 text-sm">
        <p className="font-semibold text-gray-800">{d.name}</p>
        <p className="text-gray-600 mt-0.5">
          {d.value} procédure{d.value !== 1 ? 's' : ''}
        </p>
      </div>
    );
  }
  return null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const Statistiques: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const isAdmin = isSuperAdmin();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tablePage, setTablePage] = useState(0);
  const TABLE_PAGE_SIZE = 15;
  const [listPage, setListPage] = useState(0);
  const LIST_PAGE_SIZE = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, proceduresData] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getProcedures({
            page: 0,
            size: 1000,
            // RESPONSABLE : filtre sur sa structure uniquement
            structureId: isAdmin ? undefined : (user?.structureId ?? undefined),
          }),
        ]);
        setStats(statsData);
        setProcedures(proceduresData.content);
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isAdmin, user?.structureId]);

  if (isLoading) {
    return <StatistiquesSkeleton />;
  }

  // ── Données graphiques ────────────────────────────────────────────────────

  const statusData = [
    { name: 'Terminées', value: stats?.proceduresTerminees || 0, color: '#22c55e' },
    { name: 'En cours', value: stats?.proceduresEnCours || 0, color: '#3b82f6' },
    { name: 'En attente', value: stats?.proceduresEnAttente || 0, color: '#9ca3af' },
  ];

  const progressData = [...procedures]
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 20)
    .map((p) => ({
      name: p.nom.length > 20 ? p.nom.substring(0, 20) + '...' : p.nom,
      progression: p.progress,
      ponderation: p.ponderationScore,
    }));

  const paginatedProcedures = procedures.slice(
    tablePage * TABLE_PAGE_SIZE,
    (tablePage + 1) * TABLE_PAGE_SIZE,
  );
  const tableTotalPages = Math.ceil(procedures.length / TABLE_PAGE_SIZE);

  // Utilisé uniquement pour le SUPER_ADMIN
  const structureData = procedures.reduce((acc, p) => {
    const nom = p.structureNom || 'Non assigné';
    const existing = acc.find((item) => item.name === nom);
    if (existing) existing.count++;
    else acc.push({ name: nom, count: 1 });
    return acc;
  }, [] as { name: string; count: number }[]);

  // Section "Mon équipe" pour RESPONSABLE : regrouper par responsable
  const teamData = procedures.reduce((acc, p) => {
    if (!p.responsableNom) return acc;
    const key = p.responsableId || p.responsableNom;
    const existing = acc.find((item) => item.key === key);
    if (existing) {
      existing.count++;
      existing.totalProgress += p.progress;
      existing.totalPonderation += p.ponderationScore;
    } else {
      acc.push({
        key,
        nom: p.responsableNom,
        count: 1,
        totalProgress: p.progress,
        totalPonderation: p.ponderationScore,
      });
    }
    return acc;
  }, [] as { key: string; nom: string; count: number; totalProgress: number; totalPonderation: number }[]);

  // Score pondération moyen global
  const avgPonderation =
    procedures.length > 0
      ? Math.round(procedures.reduce((s, p) => s + p.ponderationScore, 0) / procedures.length)
      : 0;

  const avgProgress =
    procedures.length > 0
      ? Math.round(procedures.reduce((s, p) => s + p.progress, 0) / procedures.length)
      : 0;

  const totalTaches = procedures.reduce((s, p) => s + p.tachesTotal, 0);
  const totalTachesCompleted = procedures.reduce((s, p) => s + p.tachesCompleted, 0);

  // ── KPI cards config ──────────────────────────────────────────────────────

  const kpiCards = [
    {
      label: 'Total Procédures',
      value: stats?.totalProcedures || 0,
      icon: DocumentTextIcon,
      iconBg: 'bg-primary-50',
      iconColor: 'text-primary-600',
      accent: 'border-t-4 border-primary-400',
    },
    isAdmin
      ? {
          label: 'Structures',
          value: stats?.totalStructures || 0,
          icon: BuildingOfficeIcon,
          iconBg: 'bg-purple-50',
          iconColor: 'text-purple-600',
          accent: 'border-t-4 border-purple-400',
        }
      : {
          label: 'Utilisateurs',
          value: stats?.totalUsers || 0,
          icon: UserGroupIcon,
          iconBg: 'bg-blue-50',
          iconColor: 'text-blue-600',
          accent: 'border-t-4 border-blue-400',
        },
    {
      label: 'Tâches en retard',
      value: stats?.tachesEnRetard || 0,
      icon: ExclamationTriangleIcon,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
      accent: 'border-t-4 border-red-400',
    },
    {
      label: 'Échéances proches',
      value: stats?.tachesProchesEcheance || 0,
      icon: ClockIcon,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      accent: 'border-t-4 border-amber-400',
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8 pb-8">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <ChartBarIcon className="h-6 w-6 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">Statistiques</h1>
          </div>
          <p className="mt-1.5 text-sm text-gray-500">
            {isAdmin
              ? "Vue d'ensemble des performances de dématérialisation — toutes structures"
              : `Performances de ${user?.structureNom || 'votre structure'}`}
          </p>
        </div>
        {!isAdmin && user?.structureNom && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 border border-primary-100 px-3 py-1.5 text-xs font-semibold text-primary-700 self-start whitespace-nowrap">
            <BuildingOfficeIcon className="h-3.5 w-3.5" />
            {user.structureNom}
          </span>
        )}
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiCards.map((kpi) => (
          <div
            key={kpi.label}
            className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${kpi.accent} p-5 hover:shadow-md transition-all duration-200 group`}
          >
            <div className={`${kpi.iconBg} rounded-xl p-3 w-fit group-hover:scale-105 transition-transform duration-200`}>
              <kpi.icon className={`h-6 w-6 ${kpi.iconColor}`} />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-3 leading-none">{kpi.value}</p>
            <p className="text-xs text-gray-400 mt-1.5 uppercase tracking-wide font-medium">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* ── Graphiques principaux ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Camembert statuts */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Répartition par statut</h2>
            <p className="text-xs text-gray-400 mt-0.5">Distribution des {stats?.totalProcedures || 0} procédures</p>
          </div>

          {(stats?.totalProcedures || 0) === 0 ? (
            <div className="flex flex-col items-center justify-center h-52 text-center">
              <InboxIcon className="h-10 w-10 text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">Aucune donnée à afficher</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) =>
                      percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''
                    }
                    outerRadius={90}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              {/* Légende personnalisée */}
              <div className="flex justify-center gap-6 mt-1">
                {statusData.map((item) => (
                  <div key={item.name} className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-xs text-gray-500">
                      {item.name}
                      <span className="font-semibold text-gray-700 ml-1">({item.value})</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* SUPER_ADMIN : procédures par structure | RESPONSABLE : avancement */}
        {isAdmin ? (
          <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-6">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">Procédures par structure</h2>
              <p className="text-xs text-gray-400 mt-0.5">{structureData.length} structure{structureData.length !== 1 ? 's' : ''} impliquée{structureData.length !== 1 ? 's' : ''}</p>
            </div>
            {structureData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-52 text-center">
                <InboxIcon className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Aucune structure enregistrée</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={structureData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar
                    dataKey="count"
                    fill="#16a34a"
                    name="Procédures"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        ) : (
          <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-6">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">Avancement des procédures</h2>
              <p className="text-xs text-gray-400 mt-0.5">{procedures.length} procédure{procedures.length !== 1 ? 's' : ''} au total</p>
            </div>
            {procedures.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-52 text-center">
                <InboxIcon className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Aucune procédure</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {procedures.slice(listPage * LIST_PAGE_SIZE, (listPage + 1) * LIST_PAGE_SIZE).map((p) => {
                    const gradientClass = getProgressBarColor(p.progress);
                    const textColorClass = getProgressTextColor(p.progress);
                    return (
                      <div key={p.id}>
                        <div className="flex items-center justify-between mb-1.5 gap-2">
                          <span className="text-sm text-gray-700 truncate flex-1 leading-tight">
                            {p.nom}
                          </span>
                          <span className={`text-sm font-bold flex-shrink-0 ${textColorClass}`}>
                            {p.progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full bg-gradient-to-r ${gradientClass} transition-all duration-500`}
                            style={{ width: `${p.progress}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {Math.ceil(procedures.length / LIST_PAGE_SIZE) > 1 && (
                  <div className="mt-4">
                    <Pagination
                      page={listPage}
                      totalPages={Math.ceil(procedures.length / LIST_PAGE_SIZE)}
                      totalElements={procedures.length}
                      size={LIST_PAGE_SIZE}
                      onPageChange={setListPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Section "Mon équipe" (RESPONSABLE uniquement) ─────────────────── */}
      {!isAdmin && teamData.length > 0 && (
        <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <UserGroupIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-base font-semibold text-gray-900">Mon équipe</h2>
            <span className="ml-auto rounded-full bg-primary-50 border border-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              {teamData.length} responsable{teamData.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamData.map((member, idx) => {
              const avgProg = Math.round(member.totalProgress / member.count);
              const avgPond = Math.round(member.totalPonderation / member.count);
              const colorClass = AVATAR_COLORS[idx % AVATAR_COLORS.length];
              const gradClass = getProgressBarColor(avgProg);
              return (
                <div
                  key={member.key}
                  className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 h-10 w-10 rounded-full ${colorClass} flex items-center justify-center text-sm font-bold`}>
                      {getInitials(member.nom)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{member.nom}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {member.count} procédure{member.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progression moy.</span>
                      <span className="font-semibold text-gray-700">{avgProg}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full bg-gradient-to-r ${gradClass}`}
                        style={{ width: `${avgProg}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Score pondération moy.</span>
                    <span className="font-bold text-primary-600">{avgPond}/100</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Graphique progression / pondération ────────────────────────────── */}
      {procedures.length > 0 && (
        <div className="bg-white shadow-sm rounded-2xl border border-gray-100 p-6">
          <div className="mb-5">
            <h2 className="text-base font-semibold text-gray-900">Progression et pondération par procédure</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Top {Math.min(20, procedures.length)} procédures par progression
              {procedures.length > 20 && ` sur ${procedures.length}`}
            </p>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(300, progressData.length * 44)}>
            <BarChart data={progressData} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={160}
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomBarTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                iconType="circle"
              />
              <Bar dataKey="progression" fill="#3b82f6" name="Progression (%)" radius={[0, 4, 4, 0]} maxBarSize={14} />
              <Bar dataKey="ponderation" fill="#22c55e" name="Score pondération" radius={[0, 4, 4, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Tableau détail ─────────────────────────────────────────────────── */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Détail des procédures</h2>
            <p className="text-xs text-gray-400 mt-0.5">{procedures.length} enregistrement{procedures.length !== 1 ? 's' : ''}</p>
          </div>
        </div>


        {procedures.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="bg-gray-100 rounded-full p-4 mb-3">
              <InboxIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">Aucune procédure enregistrée</p>
            <p className="text-xs text-gray-400 mt-1">Les procédures apparaîtront ici une fois créées.</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Procédure
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Structure
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Année
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Progression
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Pondération
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Tâches
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedProcedures.map((procedure, idx) => {
                  const progressColor = getProgressBarColor(procedure.progress);
                  const progressText = getProgressTextColor(procedure.progress);
                  const isEven = idx % 2 === 0;
                  return (
                    <tr
                      key={procedure.id}
                      className={`${isEven ? 'bg-white' : 'bg-gray-50/60'} hover:bg-primary-50/30 transition-colors duration-150 border-b border-gray-100 last:border-b-0`}
                    >
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900 leading-tight">
                          {procedure.nom}
                        </p>
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center rounded-full bg-purple-50 border border-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                            {procedure.structureNom || '—'}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                          {procedure.annee}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-20 bg-gray-100 rounded-full h-1.5 overflow-hidden flex-shrink-0">
                            <div
                              className={`h-1.5 rounded-full bg-gradient-to-r ${progressColor} transition-all duration-500`}
                              style={{ width: `${procedure.progress}%` }}
                            />
                          </div>
                          <span className={`text-sm font-bold flex-shrink-0 ${progressText}`}>
                            {procedure.progress}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-bold text-primary-600">
                            {procedure.ponderationScore}
                          </span>
                          <span className="text-xs text-gray-400">/100</span>
                          {procedure.ponderationScore >= 80 && (
                            <CheckBadgeIcon className="h-4 w-4 text-green-500" title="Excellent score" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-semibold text-gray-800">
                            {procedure.tachesCompleted}
                          </span>
                          <span className="text-xs text-gray-400">
                            /{procedure.tachesTotal}
                          </span>
                          {procedure.tachesTotal > 0 && (
                            <span className="ml-1 text-xs text-gray-400">
                              ({Math.round((procedure.tachesCompleted / procedure.tachesTotal) * 100)}%)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tableTotalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100">
              <Pagination
                page={tablePage}
                totalPages={tableTotalPages}
                totalElements={procedures.length}
                size={TABLE_PAGE_SIZE}
                onPageChange={(p) => { setTablePage(p); }}
              />
            </div>
          )}
          </>
        )}
      </div>

      {/* ── Récapitulatif global ───────────────────────────────────────────── */}
      {procedures.length > 0 && (
        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-5">
            <ChartBarIcon className="h-5 w-5 text-primary-200" />
            <h2 className="text-base font-semibold">Récapitulatif global</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
              <p className="text-2xl font-bold text-white">{avgProgress}%</p>
              <p className="text-xs text-primary-200 mt-1">Progression moyenne</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
              <p className="text-2xl font-bold text-white">{avgPonderation}</p>
              <p className="text-xs text-primary-200 mt-1">Score pondération moy.</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
              <p className="text-2xl font-bold text-white">
                {totalTachesCompleted}
                <span className="text-lg font-normal text-primary-200">/{totalTaches}</span>
              </p>
              <p className="text-xs text-primary-200 mt-1">Tâches accomplies</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
              <p className="text-2xl font-bold text-white">{stats?.proceduresTerminees || 0}</p>
              <p className="text-xs text-primary-200 mt-1">Procédures finalisées</p>
            </div>
          </div>

          {/* Barre de progression globale */}
          <div className="mt-5">
            <div className="flex justify-between text-xs text-primary-200 mb-2">
              <span>Progression globale</span>
              <span className="font-semibold text-white">{avgProgress}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-2.5 rounded-full bg-white transition-all duration-700"
                style={{ width: `${avgProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistiques;
